import { alpha, ThemeProvider } from "@mui/material/styles";
import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { buildAppTheme, type ThemeOverrides } from "./buildAppTheme";
import { buildColorPalette, buildPrimaryPalette, type ColorRole, DEFAULT_COLOR_BY_ROLE, isValidHexColor, type ShadeKey } from "./palette";
import { DEFAULT_BODY_FONT, DEFAULT_HEADLINE_FONT, DEFAULT_LABEL_FONT, DEFAULT_LOGO, FONT_OPTIONS, type FontId, LOGO_OPTIONS, type LogoId } from "./tokens";

const SHADE_KEYS: readonly ShadeKey[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

/** Typography roles the Appearance dialog can override independently. */
export type FontRole = "headline" | "body" | "label";

const COLOR_ROLES: readonly ColorRole[] = ["primary", "secondary", "tertiary", "neutral"];
const FONT_ROLES: readonly FontRole[] = ["headline", "body", "label"];

const DEFAULT_FONT_BY_ROLE: Record<FontRole, FontId> = {
	headline: DEFAULT_HEADLINE_FONT,
	body: DEFAULT_BODY_FONT,
	label: DEFAULT_LABEL_FONT,
};

// localStorage keys. `primary` keeps its original key so existing preferences carry over.
const COLOR_STORAGE_KEY: Record<ColorRole, string> = {
	primary: "preferred_primary_color",
	secondary: "preferred_secondary_color",
	tertiary: "preferred_tertiary_color",
	neutral: "preferred_neutral_color",
};
const FONT_STORAGE_KEY: Record<FontRole, string> = {
	headline: "preferred_headline_font",
	body: "preferred_body_font",
	label: "preferred_label_font",
};

const LOGO_STORAGE_KEY = "preferred_logo";

const isValidFont = (id: string): id is FontId => FONT_OPTIONS.some((f) => f.id === id);

const isValidLogo = (id: string): id is LogoId => LOGO_OPTIONS.some((l) => l.id === id);

// Exported so code outside the provider can read the saved value, not the value from mount time.
export const readStoredColor = (role: ColorRole): string => {
	try {
		const stored = window.localStorage.getItem(COLOR_STORAGE_KEY[role]);
		if (stored && isValidHexColor(stored)) {
			return stored;
		}
	} catch {
		// localStorage may be unavailable (SSR / private mode); fall through to default.
	}
	return DEFAULT_COLOR_BY_ROLE[role];
};

const writeStoredColor = (role: ColorRole, hex: string): void => {
	try {
		if (hex.toLowerCase() === DEFAULT_COLOR_BY_ROLE[role].toLowerCase()) {
			window.localStorage.removeItem(COLOR_STORAGE_KEY[role]);
		} else {
			window.localStorage.setItem(COLOR_STORAGE_KEY[role], hex);
		}
	} catch {
		// Storage write failures are non-fatal — preference simply won't persist.
	}
};

const readStoredFont = (role: FontRole): FontId => {
	try {
		const stored = window.localStorage.getItem(FONT_STORAGE_KEY[role]);
		if (stored && isValidFont(stored)) {
			return stored;
		}
	} catch {
		// localStorage may be unavailable; fall through to default.
	}
	return DEFAULT_FONT_BY_ROLE[role];
};

const writeStoredFont = (role: FontRole, id: FontId): void => {
	try {
		if (id === DEFAULT_FONT_BY_ROLE[role]) {
			window.localStorage.removeItem(FONT_STORAGE_KEY[role]);
		} else {
			window.localStorage.setItem(FONT_STORAGE_KEY[role], id);
		}
	} catch {
		// Storage write failures are non-fatal — preference simply won't persist.
	}
};

export const readStoredLogo = (): LogoId => {
	try {
		const stored = window.localStorage.getItem(LOGO_STORAGE_KEY);
		if (stored && isValidLogo(stored)) {
			return stored;
		}
	} catch {
		// localStorage may be unavailable; fall through to default.
	}
	return DEFAULT_LOGO;
};

const writeStoredLogo = (id: LogoId): void => {
	try {
		if (id === DEFAULT_LOGO) {
			window.localStorage.removeItem(LOGO_STORAGE_KEY);
		} else {
			window.localStorage.setItem(LOGO_STORAGE_KEY, id);
		}
	} catch {
		// Storage write failures are non-fatal — preference simply won't persist.
	}
};

const readAllColors = (): Record<ColorRole, string> => ({
	primary: readStoredColor("primary"),
	secondary: readStoredColor("secondary"),
	tertiary: readStoredColor("tertiary"),
	neutral: readStoredColor("neutral"),
});

const readAllFonts = (): Record<FontRole, FontId> => ({
	headline: readStoredFont("headline"),
	body: readStoredFont("body"),
	label: readStoredFont("label"),
});

interface ThemePreferenceContextValue {
	colors: Record<ColorRole, string>;
	fonts: Record<FontRole, FontId>;
	/** Selected app logo. Set by support in Appearance, or by a department look (Bayview UKG demo). */
	logo: LogoId;
	setColor: (role: ColorRole, hex: string) => void;
	setFont: (role: FontRole, id: FontId) => void;
	setLogo: (id: LogoId) => void;
	resetColor: (role: ColorRole) => void;
	resetFont: (role: FontRole) => void;
	resetAll: () => void;
	/** True when any color/font/logo axis differs from its default. */
	isCustom: boolean;
}

const ThemePreferenceContext = createContext<ThemePreferenceContextValue | null>(null);

export const ThemePreferenceProvider: FC<PropsWithChildren> = ({ children }) => {
	const [colors, setColors] = useState<Record<ColorRole, string>>(readAllColors);
	const [fonts, setFonts] = useState<Record<FontRole, FontId>>(readAllFonts);
	const [logo, setLogoState] = useState<LogoId>(readStoredLogo);

	const setColor = useCallback((role: ColorRole, hex: string) => {
		if (!isValidHexColor(hex)) return;
		setColors((prev) => ({ ...prev, [role]: hex }));
		writeStoredColor(role, hex);
	}, []);

	const setFont = useCallback((role: FontRole, id: FontId) => {
		if (!isValidFont(id)) return;
		setFonts((prev) => ({ ...prev, [role]: id }));
		writeStoredFont(role, id);
	}, []);

	const setLogo = useCallback((id: LogoId) => {
		if (!isValidLogo(id)) return;
		setLogoState(id);
		writeStoredLogo(id);
	}, []);

	const resetColor = useCallback((role: ColorRole) => {
		setColors((prev) => ({ ...prev, [role]: DEFAULT_COLOR_BY_ROLE[role] }));
		writeStoredColor(role, DEFAULT_COLOR_BY_ROLE[role]);
	}, []);

	const resetFont = useCallback((role: FontRole) => {
		setFonts((prev) => ({ ...prev, [role]: DEFAULT_FONT_BY_ROLE[role] }));
		writeStoredFont(role, DEFAULT_FONT_BY_ROLE[role]);
	}, []);

	const resetAll = useCallback(() => {
		setColors({ ...DEFAULT_COLOR_BY_ROLE });
		setFonts({ ...DEFAULT_FONT_BY_ROLE });
		setLogoState(DEFAULT_LOGO);
		for (const role of COLOR_ROLES) writeStoredColor(role, DEFAULT_COLOR_BY_ROLE[role]);
		for (const role of FONT_ROLES) writeStoredFont(role, DEFAULT_FONT_BY_ROLE[role]);
		writeStoredLogo(DEFAULT_LOGO);
	}, []);

	const overrides = useMemo<ThemeOverrides>(
		() => ({
			primary: colors.primary,
			secondary: colors.secondary,
			tertiary: colors.tertiary,
			neutral: colors.neutral,
			headlineFont: fonts.headline,
			bodyFont: fonts.body,
			labelFont: fonts.label,
		}),
		[colors, fonts],
	);

	const theme = useMemo(() => buildAppTheme(overrides), [overrides]);

	// Expose every color role's shade ladder as CSS variables on :root so files
	// that use HTML `style={...}` (no MUI theme access) can opt in via
	// `var(--inhouse-secondary-300)` etc. Repainted instantly on change.
	useEffect(() => {
		const root = document.documentElement;

		for (const role of COLOR_ROLES) {
			// `primary` carries hand-picked default light/dark stops — keep them.
			const palette = role === "primary" ? buildPrimaryPalette(colors[role]) : buildColorPalette(colors[role]);
			for (const key of SHADE_KEYS) {
				root.style.setProperty(`--inhouse-${role}-${key}`, palette.shades[key]);
			}
			root.style.setProperty(`--inhouse-${role}`, palette.main);
			root.style.setProperty(`--inhouse-${role}-light`, palette.light);
			root.style.setProperty(`--inhouse-${role}-dark`, palette.dark);
			root.style.setProperty(`--inhouse-${role}-contrast`, palette.contrastText);
		}

		// Primary alpha aliases — named `*-alpha-NN` to avoid collision with the
		// `*-NN` shade ladder above. Prefer `alpha(theme.palette.primary[500], 0.NN)`
		// from MUI or `color-mix(in srgb, var(--inhouse-primary) NN%, transparent)`
		// in raw CSS for new code; these exist for call sites that can't access
		// the MUI theme.
		const primary = buildPrimaryPalette(colors.primary);
		root.style.setProperty("--inhouse-primary-alpha-08", alpha(primary.main, 0.08));
		root.style.setProperty("--inhouse-primary-alpha-16", alpha(primary.main, 0.16));
		root.style.setProperty("--inhouse-primary-alpha-20", alpha(primary.main, 0.2));
		root.style.setProperty("--inhouse-primary-alpha-24", alpha(primary.main, 0.24));
		root.style.setProperty("--inhouse-primary-alpha-50", alpha(primary.main, 0.5));
		root.style.setProperty("--inhouse-primary-alpha-70", alpha(primary.main, 0.7));
		root.style.setProperty("--inhouse-primary-dark-alpha-50", alpha(primary.dark, 0.5));
		root.style.setProperty("--inhouse-primary-dark-alpha-60", alpha(primary.dark, 0.6));
		root.style.setProperty("--inhouse-primary-light-alpha-50", alpha(primary.light, 0.5));
		root.style.setProperty("--inhouse-primary-light-alpha-70", alpha(primary.light, 0.7));
	}, [colors]);

	const value = useMemo<ThemePreferenceContextValue>(() => {
		const colorsCustom = COLOR_ROLES.some((role) => colors[role].toLowerCase() !== DEFAULT_COLOR_BY_ROLE[role].toLowerCase());
		const fontsCustom = FONT_ROLES.some((role) => fonts[role] !== DEFAULT_FONT_BY_ROLE[role]);
		return {
			colors,
			fonts,
			logo,
			setColor,
			setFont,
			setLogo,
			resetColor,
			resetFont,
			resetAll,
			isCustom: colorsCustom || fontsCustom || logo !== DEFAULT_LOGO,
		};
	}, [colors, fonts, logo, setColor, setFont, setLogo, resetColor, resetFont, resetAll]);

	return (
		<ThemePreferenceContext.Provider value={value}>
			<ThemeProvider theme={theme}>{children}</ThemeProvider>
		</ThemePreferenceContext.Provider>
	);
};

export const useThemePreference = (): ThemePreferenceContextValue => {
	const ctx = useContext(ThemePreferenceContext);
	if (!ctx) {
		throw new Error("useThemePreference must be used inside ThemePreferenceProvider");
	}
	return ctx;
};
